/**
 * Utility functions for handling subscription-related operations on the frontend
 */
import { apiCall } from '../../utils/api';
/**
 * Fetch the user's current subscription status
 * 
 * @param {string} userId - The user's ID
 * @param {string} appId - The application ID (saleswit or saleswit)
 * @returns {Promise} - The subscription data or null if error
 */

export const fetchUserSubscription = async (userId, appId = 'saleswit') => {
  try {
    if (!userId) return null;
    
    const response = await apiCall(`/api/subscriptions/user/${userId}?app_id=${appId}`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch subscription');
    }
    
    const data = await response.json();
    return data.subscription;
  } catch (error) {
    console.error('Error fetching subscription:', error);
    return null;
  }
};

/**
 * Check if a user has enough quota for a specific action
 * 
 * @param {string} userId - The user's ID
 * @param {object} subscription - The user's subscription object
 * @param {string} actionType - The type of action (documents, battlecards, etc.)
 * @param {number} currentUsage - The current usage count
 * @param {number} additionalUsage - Additional usage to check for (default: 1)
 * @returns {boolean} - True if the user has enough quota, false otherwise
 */
export const checkSubscriptionLimit = (
  userId, 
  subscription, 
  actionType, 
  currentUsage, 
  additionalUsage = 1
) => {
  if (!subscription || !subscription.features) {
    // Default to free plan limits if no subscription
    if (subscription.app_id === 'saleswit') {
      const freeLimits = { documents: 5, battlecards: 10, users: 1 };
      return (currentUsage + additionalUsage) <= (freeLimits[actionType] || 0);
    } else {
      const freeLimits = { documents: 5, queries: 10, users: 1 };
      return (currentUsage + additionalUsage) <= (freeLimits[actionType] || 0);
    }
  }
  
  const limit = subscription.features[actionType] || 0;
  return (currentUsage + additionalUsage) <= limit;
};

export const getUsagePercentage = (subscription, actionType, currentUsage) => {
  if (!subscription || !subscription.features) {
    return 0;
  }

  const limit = subscription.features[actionType] || 0;
  
  // Calculate percentage based on remaining quota
  const percentage = limit > 0 ? Math.min(100, Math.max(0, (currentUsage / limit) * 100)) : 0;
  
  return percentage;
};

/**
 * Format a subscription price for display
 * 
 * @param {number} amount - The amount in smallest currency unit (e.g., paisa for INR)
 * @param {string} currency - The currency code (default: INR)
 * @param {string} interval - The billing interval (month/year)
 * @returns {string} - Formatted price string
 */
export const formatSubscriptionPrice = (amount, currency = 'INR', interval = null) => {
  // Convert paisa to rupees for INR
  const displayAmount = amount;
  
  const formattedPrice = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(displayAmount);
  
  if (interval) {
    return `${formattedPrice}/${interval}`;
  }
  
  return formattedPrice;
};

/**
 * Create a new subscription for a user
 * 
 * @param {string} userId - The user's ID
 * @param {string} planId - The plan ID to subscribe to
 * @param {string} appId - The application ID
 * @returns {Promise} - The subscription data or error
 */
export const createSubscription = async (userId, planId, appId = 'saleswit') => {
  try {
    // Proper logging before API call
    
    
    // Make sure planId is a string
    const planIdString = String(planId).trim();
    
    // Calculate the redirect URL to our static redirect.html page
    const redirectUrl = window.location.origin + '/redirect.html';
    
    // Make API call - define response variable before using it
    const response = await apiCall('/api/subscriptions/create', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        user_id: userId,
        plan_id: planIdString,
        app_id: appId,
        redirect_url: redirectUrl
      }),
    });
    
    // Now it's safe to log the response AFTER it's defined
    
    if (!response.ok) {
      // Get error details from response if possible
      let errorDetails = '';
      try {
        const errorData = await response.json();
        errorDetails = errorData.error || errorData.message || '';
      } catch (e) {
        // Ignore JSON parse errors
      }
      
      console.error(`Subscription creation failed with status ${response.status}: ${errorDetails}`);
      throw new Error(`Failed to create subscription: ${errorDetails || response.statusText}`);
    }
    
    const data = await response.json();
    
    // Check if we need to redirect to Razorpay checkout
    if (data.subscription && data.subscription.short_url) {
      window.location.href = data.subscription.short_url;
      return { redirecting: true };
    }
    
    return data.subscription;
  } catch (error) {
    console.error('Error creating subscription:', error);
    throw error;
  }
};

/**
 * Cancel a subscription
 * 
 * @param {string} userId - The user's ID
 * @param {string} subscriptionId - The subscription ID to cancel
 * @returns {Promise} - The result of the cancellation
 */
export const cancelSubscription = async (userId, subscriptionId) => {
  try {
    const response = await apiCall(`/api/subscriptions/cancel/${subscriptionId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        user_id: userId
      }),
    });
    
    if (!response.ok) {
      // Try to get error details from the response
      let errorDetails = '';
      try {
        const errorData = await response.json();
        errorDetails = errorData.error ? `: ${errorData.error}` : '';
      } catch (e) {
        // Ignore JSON parsing errors and continue
      }
      
      throw new Error(`Failed to cancel subscription${errorDetails}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error cancelling subscription:', error);
    throw error;
  }
};

/**
 * Get all available plans for an app
 * 
 * @param {string} appId - The application ID
 * @returns {Promise} - Array of available plans
 */
export const getAvailablePlans = async (appId = 'saleswit') => {
  try {
    const response = await apiCall(`/api/subscriptions/plans?app_id=${appId}`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch plans');
    }
    
    const data = await response.json();
    return data.plans || [];
  } catch (error) {
    console.error('Error fetching plans:', error);
    return [];
  }
};

/**
 * Get resource usage limits from a subscription
 * 
 * @param {object} subscription - The user's subscription object
 * @returns {object} - Resource limits
 */
export const getResourceLimits = (subscription) => {
  if (!subscription || !subscription.features) {
    // Default limits for free plan
    return {
      document_pages: 50,
      perplexity_requests: 20
    };
  }
  
  return {
    document_pages: subscription.features.document_pages || 50,
    perplexity_requests: subscription.features.perplexity_requests || 20
  };
};

/**
 * Check if a user has enough resource quota
 * 
 * @param {string} userId - The user's ID
 * @param {object} subscription - The user's subscription object
 * @param {string} resourceType - The type of resource (document_pages, perplexity_requests)
 * @param {number} currentUsage - The current usage count
 * @param {number} additionalUsage - Additional usage to check for (default: 1)
 * @returns {boolean} - True if the user has enough quota, false otherwise
 */
export const checkResourceLimit = (
  userId, 
  subscription, 
  resourceType, 
  currentUsage, 
  additionalUsage = 1
) => {
  const limits = getResourceLimits(subscription);
  const limit = limits[resourceType] || 0;
  return (currentUsage + additionalUsage) <= limit;
};

/**
 * Get resource quota for a user
 * 
 * @param {string} userId - The user's ID
 * @param {string} appId - The application ID
 * @returns {Promise} - Resource quota
 */
export const getResourceQuota = async (userId, appId = 'saleswit') => {
  try {
    const response = await apiCall(`/api/subscriptions/resource-quota?user_id=${userId}&app_id=${appId}`);
    
    if (!response.ok) {
      throw new Error('Failed to get resource quota');
    }
    
    const data = await response.json();
    return data.quota || {};
  } catch (error) {
    console.error('Error getting resource quota:', error);
    return {};
  }
};

/**
 * Check if a resource is available
 * 
 * @param {string} userId - The user's ID
 * @param {string} appId - The application ID
 * @param {string} resourceType - The type of resource (document_pages, perplexity_requests for saleswit, requests for saleswit)
 * @param {number} count - Amount to check for
 * @returns {Promise} - Result of check
 */
export const checkResourceAvailability = async (userId, appId = 'saleswit', resourceType, count = 1) => {
  try {
    const response = await apiCall('/api/subscriptions/check-resource', {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId,
        app_id: appId,
        resource_type: resourceType,
        count: count
      })
    });
    
    if (!response.ok) {
      throw new Error('Failed to check resource availability');
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error checking resource availability:', error);
    return { available: false, error: error.message };
  }
};

/**
 * Decrement resource quota
 * 
 * @param {string} userId - The user's ID
 * @param {string} appId - The application ID
 * @param {string} resourceType - The type of resource (document_pages, perplexity_requests for saleswit, requests for saleswit)
 * @param {number} count - Amount to decrement by
 * @returns {Promise} - Result of operation
 */
export const decrementResourceQuota = async (userId, appId = 'saleswit', resourceType, count = 1) => {
  try {
    const response = await apiCall('/api/subscriptions/decrement-resource', {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId,
        app_id: appId,
        resource_type: resourceType,
        count: count
      })
    });
    
    if (!response.ok) {
      throw new Error('Failed to decrement resource quota');
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error decrementing resource quota:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Initialize or reset resource quota
 * 
 * @param {string} userId - The user's ID
 * @param {string} appId - The application ID
 * @returns {Promise} - Result of operation
 */
export const initializeResourceQuota = async (userId, appId = 'saleswit') => {
  try {
    const response = await apiCall('/api/subscriptions/initialize-quota', {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId,
        app_id: appId
      })
    });
    
    if (!response.ok) {
      throw new Error('Failed to initialize resource quota');
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error initializing resource quota:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get default resource limits based on app
 * 
 * @param {string} appId - The application ID
 * @returns {object} - Default resource limits
 */
export const getDefaultResourceLimits = (appId = 'saleswit') => {
  if (appId === 'marketfit') {
    return {
      document_pages: 50,
      perplexity_requests: 20
    };
  } else {  // saleswit
    return {
      requests: 3
    };
  }
};

/**
 * Ensure user has resource quota for the app
 * 
 * @param {string} userId - The user's ID
 * @param {string} appId - The application ID
 * @returns {Promise} - Result of operation
 */
export const ensureUserResourceQuota = async (userId, appId = 'saleswit') => {
  try {
    const response = await apiCall('/api/subscriptions/ensure-resource-quota', {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId,
        app_id: appId
      })
    });
    
    if (!response.ok) {
      throw new Error('Failed to ensure resource quota');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error ensuring resource quota:', error);
    return { success: false, error: error.message };
  }
};